import axios from 'axios';

const TOKEN_KEY = 'georges_token';
export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Ajoute le jeton d'authentification à une URL de téléchargement direct
// (PDF, exports CSV ouverts par le navigateur, qui ne passent pas par axios).
export const withToken = (url) => {
  const t = getToken();
  if (!t) return url;
  return url + (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(t);
};

// En local : proxy vers localhost:3001 (via "proxy" dans package.json)
// En production : même origine, URL relative
const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Intercepteur d'ajout du jeton (Bearer) à chaque requête
const attachToken = (config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
};

// Déconnexion automatique si le jeton est invalide/expiré (401)
const handle401 = (err) => {
  if (err.response && err.response.status === 401 && localStorage.getItem(TOKEN_KEY)) {
    localStorage.removeItem('georges_user');
    localStorage.removeItem(TOKEN_KEY);
    if (window.location.pathname !== '/') window.location.assign('/');
    else window.location.reload();
  }
  return Promise.reject(err);
};

// On applique les intercepteurs à l'instance dédiée ET à l'instance axios par défaut
// (certains composants — Alertes, Georges — appellent axios directement).
api.interceptors.request.use(attachToken);
api.interceptors.response.use((res) => res, handle401);
axios.interceptors.request.use(attachToken);
axios.interceptors.response.use((res) => res, handle401);

// Structures
export const structuresApi = {
  getAll: () => api.get('/structures'),
  getById: (id) => api.get(`/structures/${id}`),
  update: (id, data) => api.put(`/structures/${id}`, data),
  updateCharte: (id, data) => api.put(`/structures/${id}/charte`, data),
  uploadLogo: (id, formData) => api.post(`/structures/${id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: (id) => api.delete(`/structures/${id}/logo`),
  getLogoUrl: (id, bust) => withToken(`/api/structures/${id}/logo/image?ts=${bust || Date.now()}`),
};

// Enfants
export const enfantsApi = {
  getAll: (params) => api.get('/enfants', { params }),
  getById: (id) => api.get(`/enfants/${id}`),
  create: (data) => api.post('/enfants', data),
  update: (id, data) => api.put(`/enfants/${id}`, data),
  delete: (id) => api.delete(`/enfants/${id}`),
  setArchive: (id, archive) => api.patch(`/enfants/${id}/archive`, { archive }),
  archiveDossierUrl: (file) => withToken(`/api/enfants/archives/${file}`),
  genererAttestation: (id, mois) => api.post(`/enfants/${id}/attestation`, { mois }),
  getAttestationPdfUrl: (id, mois) => withToken(`/api/enfants/${id}/attestation/pdf${mois ? `?mois=${mois}` : ''}`),
  getContratPdfUrl: (id) => withToken(`/api/enfants/${id}/contrat/pdf`),
  getAvenantPdfUrl: (id) => withToken(`/api/enfants/${id}/avenant/pdf`),
  getAvenantStatut: (id) => api.get(`/enfants/${id}/avenant/statut`),
  verifierAvenant: (id, pieceId) => api.post(`/enfants/${id}/avenant/verifier`, { piece_id: pieceId }),
  validerAvenant: (id, motif) => api.post(`/enfants/${id}/avenant/valider`, { motif }),
  getPlanning: (id) => api.get(`/enfants/${id}/planning`),
  setContratPlanning: (id, jours) => api.put(`/enfants/${id}/planning/contrat`, { jours }),
  addPlanningException: (id, data) => api.post(`/enfants/${id}/planning/exception`, data),
  deletePlanningException: (id, excId) => api.delete(`/enfants/${id}/planning/exception/${excId}`),
  getFiche: (id) => api.get(`/enfants/${id}/fiche`),
  getFichePdfUrl: (id, sections) => withToken(`/api/enfants/${id}/fiche/pdf?sections=${(sections || ['presence', 'sante', 'contacts']).join(',')}`),
  getDossierPdfUrl: (id, include) => withToken(`/api/enfants/${id}/dossier/pdf${include && include.length ? `?include=${include.join(',')}` : ''}`),
  getEmails: (id) => api.get(`/enfants/${id}/emails`),
};

export const mailsApi = {
  status: () => api.get('/mails/status'),
  sync: () => api.post('/mails/sync'),
};

export const parametresApi = {
  get: () => api.get('/parametres'),
  getBareme: () => api.get('/parametres/bareme-cmg'),
  getBaremeHistorique: () => api.get('/parametres/bareme-cmg/historique'),
  updateBareme: (data) => api.put('/parametres/bareme-cmg', data),
  setFinancement: (structureId, mode) => api.put(`/parametres/structures/${structureId}/financement`, { mode_financement: mode }),
  getMailRecrutement: () => api.get('/parametres/mail-recrutement'),
  setMailRecrutement: (data) => api.put('/parametres/mail-recrutement', data),
  testMailRecrutement: (data) => api.post('/parametres/mail-recrutement/test', data || {}),
  getKdrive: () => api.get('/parametres/kdrive-backup'),
  setKdrive: (data) => api.put('/parametres/kdrive-backup', data),
  testKdrive: (data) => api.post('/parametres/kdrive-backup/test', data || {}),
};

export const echeancesApi = {
  getAll: (params) => api.get('/echeances', { params }),
  getResume: (params) => api.get('/echeances/resume', { params }),
  create: (data) => api.post('/echeances', data),
  update: (id, data) => api.put(`/echeances/${id}`, data),
  updateStatut: (id, statut) => api.patch(`/echeances/${id}/statut`, { statut }),
  delete: (id) => api.delete(`/echeances/${id}`),
};

// Devis
export const devisApi = {
  getAll: (params) => api.get('/devis', { params }),
  getById: (id) => api.get(`/devis/${id}`),
  create: (data) => api.post('/devis', data),
  update: (id, data) => api.put(`/devis/${id}`, data),
  updateStatut: (id, statut) => api.patch(`/devis/${id}/statut`, { statut }),
  delete: (id) => api.delete(`/devis/${id}`),
  getPdfUrl: (id) => withToken(`/api/devis/${id}/pdf`),
  analyseRentabilite: (data) => api.post('/devis/analyse-rentabilite', data),
};

// Contrats
export const contratsApi = {
  getAll: (params) => api.get('/contrats', { params }),
  getById: (id) => api.get(`/contrats/${id}`),
  create: (data) => api.post('/contrats', data),
  update: (id, data) => api.put(`/contrats/${id}`, data),
  updateStatut: (id, statut) => api.patch(`/contrats/${id}/statut`, { statut }),
  getPdfUrl: (id) => withToken(`/api/contrats/${id}/pdf`),
};

// Employes
export const employesApi = {
  getAll: (params) => api.get('/employes', { params }),
  getById: (id) => api.get(`/employes/${id}`),
  create: (data) => api.post('/employes', data),
  update: (id, data) => api.put(`/employes/${id}`, data),
  delete: (id) => api.delete(`/employes/${id}`),
  transfert: (id, structure_id) => api.patch(`/employes/${id}/transfert`, { structure_id }),
  updateHeuresSupp: (id, heures) => api.patch(`/employes/${id}/heures-supp`, { heures }),
  getHeuresSuppLog: (id) => api.get(`/employes/${id}/heures-supp-log`),
  addHeuresSuppLog: (id, data) => api.post(`/employes/${id}/heures-supp-log`, data),
  deleteHeuresSuppLog: (logId) => api.delete(`/employes/heures-supp-log/${logId}`),
  getAbsences: (id) => api.get(`/employes/${id}/absences`),
  getAllAbsences: (params) => api.get('/employes/absences/all', { params }),
  createAbsence: (data) => api.post('/employes/absences', data),
  updateAbsenceStatut: (id, statut) => api.patch(`/employes/absences/${id}/statut`, { statut }),
  getReleveMensuel: (params) => api.get('/employes/releve-mensuel', { params }),
  getExportEmployesCsvUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return withToken(`/api/employes/export/csv?${qs}`);
  },
  getExportAbsencesCsvUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return withToken(`/api/employes/absences/export/csv?${qs}`);
  },
  getExportPlanningCsvUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return withToken(`/api/employes/planning/export/csv?${qs}`);
  },
};

// Factures
export const facturesApi = {
  getAll: (params) => api.get('/factures', { params }),
  getById: (id) => api.get(`/factures/${id}`),
  getStats: (params) => api.get('/factures/stats', { params }),
  create: (data) => api.post('/factures', data),
  update: (id, data) => api.put(`/factures/${id}`, data),
  updateStatut: (id, statut, mode) => api.patch(`/factures/${id}/statut`, { statut, mode_paiement: mode }),
  valider: (id) => api.post(`/factures/${id}/valider`),
  getFecUrl: (structureId, annee) => withToken(`/api/factures/fec?structure_id=${structureId}&annee=${annee}`),
  getFacturxUrl: (id) => withToken(`/api/factures/${id}/facturx.xml`),
  generateMonthly: (mois, annee) => api.post('/factures/generate-monthly', { mois, annee }),
  getPdfUrl: (id) => withToken(`/api/factures/${id}/pdf`),
  getArrhes: () => api.get('/factures/arrhes/all'),
  getExportUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return withToken(`/api/factures/export/csv?${qs}`);
  },
};

export const entreprisesApi = {
  getAll: (params) => api.get('/entreprises', { params }),
  create: (data) => api.post('/entreprises', data),
  update: (id, data) => api.put(`/entreprises/${id}`, data),
  delete: (id) => api.delete(`/entreprises/${id}`),
};

export const transmissionsApi = {
  getAll: (params) => api.get('/transmissions', { params }),
  create: (data) => api.post('/transmissions', data),
  update: (id, data) => api.put(`/transmissions/${id}`, data),
  delete: (id) => api.delete(`/transmissions/${id}`),
  dictee: (transcript) => api.post('/transmissions/dictee', { transcript }),
};

export const developpementApi = {
  getByEnfant: (enfantId) => api.get('/developpement', { params: { enfant_id: enfantId } }),
  create: (data) => api.post('/developpement', data),
  update: (id, data) => api.put(`/developpement/${id}`, data),
  delete: (id) => api.delete(`/developpement/${id}`),
};

export const documentsModelesApi = {
  list: () => api.get('/documents-modeles'),
  getPdfUrl: (enfantId, cles) => withToken(`/api/documents-modeles/pdf?enfant_id=${enfantId}&docs=${(cles || []).join(',')}`),
};

export const documentsApi = {
  getAll: (params) => api.get('/documents', { params }),
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadMultiple: (formData) => api.post('/documents/upload-multiple', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  addTexte: (data) => api.post('/documents/texte', data),
  delete: (id) => api.delete(`/documents/${id}`),
};

export const courriersApi = {
  getAll: () => api.get('/courriers'),
  getById: (id) => api.get(`/courriers/${id}`),
  create: (data) => api.post('/courriers', data),
  update: (id, data) => api.put(`/courriers/${id}`, data),
  delete: (id) => api.delete(`/courriers/${id}`),
  importFile: (formData) => api.post('/courriers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  apercu: (id, enfantId) => api.get(`/courriers/${id}/apercu`, { params: enfantId ? { enfant_id: enfantId } : {} }),
  pdfUrl: (id, enfantId) => withToken(`/api/courriers/${id}/pdf${enfantId ? `?enfant_id=${enfantId}` : ''}`),
};

export const whatsappApi = {
  connect: (structureId) => api.post('/whatsapp/connect', { structure_id: structureId }),
  status: (structureId) => api.get('/whatsapp/status', { params: { structure_id: structureId } }),
  disconnect: (structureId) => api.post('/whatsapp/disconnect', { structure_id: structureId }),
  chats: (structureId) => api.get('/whatsapp/chats', { params: { structure_id: structureId } }),
  messages: (structureId, chat) => api.get('/whatsapp/messages', { params: { structure_id: structureId, chat } }),
  send: (structureId, chat, text) => api.post('/whatsapp/send', { structure_id: structureId, chat, text }),
  sendMedia: (structureId, chat, file, caption) => {
    const fd = new FormData();
    fd.append('fichier', file);
    fd.append('structure_id', structureId);
    fd.append('chat', chat);
    if (caption) fd.append('caption', caption);
    return api.post('/whatsapp/send-media', fd);
  },
};

export const analyticsApi = {
  getSynthese: () => api.get('/analytics/synthese'),
  getOptimisations: () => api.post('/analytics/optimisations', {}),
  getStatistiques: () => api.get('/analytics/statistiques'),
};

export const planningApi = {
  getOccupation: (structureId, dateDebut) => api.get('/planning/occupation', { params: { structure_id: structureId, date_debut: dateDebut } }),
  getPrevisionnel: (structureId, dateDebut, semaine) => api.get('/planning/previsionnel', { params: { structure_id: structureId, date_debut: dateDebut, semaine } }),
  getModifications: (params) => api.get('/planning/modifications', { params }),
  getEnfants: (structureId, semaine) => api.get('/planning/enfants', { params: { structure_id: structureId, semaine } }),
  getAujourdhui: (structureId) => api.get('/planning/aujourdhui', { params: { structure_id: structureId } }),
  getEquipeAujourdhui: (structureId) => api.get('/planning/equipe-aujourdhui', { params: { structure_id: structureId } }),
  addException: (data) => api.post('/planning/exception', data),
  deleteException: (id) => api.delete(`/planning/exception/${id}`),
  getSlots: (params) => api.get('/planning', { params }),
  createSlot: (data) => api.post('/planning', data),
  deleteSlot: (id) => api.delete(`/planning/${id}`),
  notifier: (data) => api.post('/planning/notifier', data),
  getTemplate: (structureId) => api.get(`/planning/template/${structureId}`),
  createTemplateSlot: (data) => api.post('/planning/template', data),
  deleteTemplateSlot: (id) => api.delete(`/planning/template/${id}`),
  genererIA: (data) => api.post('/planning/generer-ia', data),
  appliquerTemplate: (data) => api.post('/planning/appliquer-template', data),
};

export const preinscriptionsApi = {
  getAll: (params) => api.get('/preinscriptions', { params }),
  getById: (id) => api.get(`/preinscriptions/${id}`),
  create: (data) => api.post('/preinscriptions', data),
  update: (id, data) => api.put(`/preinscriptions/${id}`, data),
  delete: (id) => api.delete(`/preinscriptions/${id}`),
};

export const protocolesApi = {
  getAll: (params) => api.get('/protocoles', { params }),
  getById: (id) => api.get(`/protocoles/${id}`),
  create: (data) => api.post('/protocoles', data),
  update: (id, data) => api.put(`/protocoles/${id}`, data),
  updateStatut: (id, statut) => api.patch(`/protocoles/${id}/statut`, { statut }),
  delete: (id) => api.delete(`/protocoles/${id}`),
  genererIa: (id) => api.post(`/protocoles/${id}/generer-ia`),
  upload: (id, formData) => api.post(`/protocoles/${id}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  figer: (id) => api.post(`/protocoles/${id}/figer`),
  deverrouiller: (id) => api.post(`/protocoles/${id}/deverrouiller`),
  marquerLu: (id, employeId) => api.post(`/protocoles/${id}/lecture`, { employe_id: employeId }),
  retirerLecture: (id, employeId) => api.delete(`/protocoles/${id}/lecture/${employeId}`),
  getPdfUrl: (id) => withToken(`/api/protocoles/${id}/pdf`),
  getPdfDownloadUrl: (id) => withToken(`/api/protocoles/${id}/pdf?download=1`),
};

export const signaturesApi = {
  getStatus: (type, refId) => api.get('/signatures', { params: { type, ref_id: refId } }),
  listAll: () => api.get('/signatures'),
  envoyerLibre: (formData) => api.post('/signatures/envoyer-libre', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  envoyer: (data) => api.post('/signatures/envoyer', data),
  sync: (id) => api.post(`/signatures/${id}/sync`),
  getDocumentUrl: (id) => withToken(`/api/signatures/${id}/document`),
};

// Mandats de prélèvement SEPA (admin/direction)
export const sepaApi = {
  get: (enfantId) => api.get(`/sepa/enfants/${enfantId}`),
  save: (enfantId, data) => api.put(`/sepa/enfants/${enfantId}`, data),
  lireRib: (enfantId) => api.post(`/sepa/enfants/${enfantId}/lire-rib`),
  mandatPdfUrl: (enfantId) => withToken(`/api/sepa/enfants/${enfantId}/mandat/pdf`),
};

// Signature électronique native — endpoints PUBLICS (accès par jeton, sans compte)
export const signaturePubliqueApi = {
  get: (token) => api.get(`/public/signatures/${token}`),
  documentUrl: (token) => `/api/public/signatures/${token}/document`,
  otp: (token) => api.post(`/public/signatures/${token}/otp`),
  signer: (token, data) => api.post(`/public/signatures/${token}/signer`, data),
};

export const authApi = {
  login: (identifiant, password) => api.post('/auth/login', { identifiant, password }),
  forgot: (email) => api.post('/auth/forgot', { email }),
  reset: (token, password) => api.post('/auth/reset', { token, password }),
  changerMdp: (ancien, nouveau) => api.post('/auth/changer-mdp', { ancien, nouveau }),
};

export const usersApi = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  renvoyerInvitation: (id, password) => api.post(`/users/${id}/renvoyer-invitation`, { password }),
  delete: (id) => api.delete(`/users/${id}`),
};

export const journalSuppressionsApi = {
  list: (params) => api.get('/journal-suppressions', { params }),
};

export const envoiDocumentsApi = {
  envoyer: (data) => api.post('/envoi-document/envoyer', data),
};

export const recrutementsApi = {
  getAll: (params) => api.get('/recrutements', { params }),
  getById: (id) => api.get(`/recrutements/${id}`),
  create: (data) => api.post('/recrutements', data),
  update: (id, data) => api.put(`/recrutements/${id}`, data),
  setStatut: (id, statut) => api.patch(`/recrutements/${id}/statut`, { statut }),
  addNote: (id, data) => api.post(`/recrutements/${id}/notes`, data),
  deleteNote: (noteId) => api.delete(`/recrutements/notes/${noteId}`),
  getModelesReponse: (id) => api.get(`/recrutements/${id}/modeles-reponse`),
  repondre: (id, data) => api.post(`/recrutements/${id}/repondre`, data),
  relever: () => api.post('/recrutements/relever'),
  embaucher: (id, data) => api.post(`/recrutements/${id}/embaucher`, data || {}),
  delete: (id) => api.delete(`/recrutements/${id}`),
};

export const piecesJointesApi = {
  list: (entiteType, entiteId) => api.get('/pieces-jointes', { params: { entite_type: entiteType, entite_id: entiteId } }),
  upload: (formData) => api.post('/pieces-jointes/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadUrl: (id) => withToken(`/api/pieces-jointes/${id}/download`),
  viewUrl: (id) => withToken(`/api/pieces-jointes/${id}/download?inline=1`),
  delete: (id) => api.delete(`/pieces-jointes/${id}`),
  extraire: (id) => api.post(`/pieces-jointes/${id}/extraire`),
  setCategorie: (id, categorie) => api.patch(`/pieces-jointes/${id}`, { categorie }),
  analyser: (formData) => api.post('/pieces-jointes/analyser', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  classer: (data) => api.post('/pieces-jointes/classer', data),
  jeter: (temp) => api.post('/pieces-jointes/jeter', { temp }),
};

export const marqueApi = {
  get: () => api.get('/marque'),
  update: (data) => api.put('/marque', data),
  uploadLogo: (formData) => api.post('/marque/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => api.delete('/marque/logo'),
  getLogoUrl: (bust) => withToken(`/api/marque/logo/image?ts=${bust || Date.now()}`),
};

export const demandesApi = {
  getAll: (params) => api.get('/demandes', { params }),
  getById: (id) => api.get(`/demandes/${id}`),
  creerManuel: (data) => api.post('/demandes/manuel', data),
  poll: () => api.post('/demandes/poll'),
  reanalyser: (id, data) => api.post(`/demandes/${id}/reanalyser`, data || {}),
  updateStatut: (id, statut) => api.patch(`/demandes/${id}/statut`, { statut }),
  setReservation: (id, place_reservee) => api.patch(`/demandes/${id}/reservation`, { place_reservee }),
  convertir: (id) => api.post(`/demandes/${id}/convertir`),
  delete: (id, motif) => api.delete(`/demandes/${id}`, { data: { motif } }),
  purgerInscrits: () => api.post('/demandes/purge-inscrits'),
  getReponse: (id) => api.get(`/demandes/${id}/reponse`),
  repondre: (id, data) => api.post(`/demandes/${id}/repondre`, data),
};

// Modèles de réponse (« mails types »)
export const modelesApi = {
  getAll: () => api.get('/modeles'),
  create: (data) => api.post('/modeles', data),
  update: (id, data) => api.put(`/modeles/${id}`, data),
  delete: (id) => api.delete(`/modeles/${id}`),
};

export const tarifsApi = {
  getAll: (params) => api.get('/tarifs', { params }),
  lookup: (heuresHebdo, structureId) => api.get('/tarifs/lookup', { params: { heures_hebdo: heuresHebdo, structure_id: structureId } }),
  create: (data) => api.post('/tarifs', data),
  update: (id, data) => api.put(`/tarifs/${id}`, data),
  delete: (id) => api.delete(`/tarifs/${id}`),
};

export const refacturationsApi = {
  getAll: (params) => api.get('/refacturations', { params }),
  simuler: (data) => api.post('/refacturations/simuler', data),
  create: (data) => api.post('/refacturations', data),
  delete: (id) => api.delete(`/refacturations/${id}`),
  getExportCsvUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return withToken(`/api/refacturations/export/csv?${qs}`);
  },
  analyser: (mois, annee) => api.post('/refacturations/analyser', { mois, annee }),
  valider: (id) => api.patch(`/refacturations/${id}/valider`),
  validerMois: (mois, annee) => api.post('/refacturations/valider-mois', { mois, annee }),
  signer: (data) => api.post('/refacturations/signer', data),
  getConventionPdfUrl: (params) => {
    const qs = new URLSearchParams(params).toString();
    return withToken(`/api/refacturations/convention/pdf?${qs}`);
  },
};

export const alertesApi = {
  getAll: () => api.get('/alertes'),
  getNonLues: (sessionId) => api.get(`/alertes/non-lues?session_id=${sessionId}`),
  create: (data) => api.post('/alertes', data),
  update: (id, data) => api.put(`/alertes/${id}`, data),
  delete: (id) => api.delete(`/alertes/${id}`),
  marquerLue: (id, sessionId) => api.post(`/alertes/${id}/lue`, { session_id: sessionId }),
  marquerToutesLues: (sessionId) => api.post('/alertes/toutes-lues', { session_id: sessionId }),
};

export default api;
